import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { IPortfolioPerformanceAccountReport } from '../portfolio-performance.types';
import { buildReferenceReport, DailyClose, injectLiveDay } from './build-reference-report';
import { ReferenceStrategyToken } from './use-reference-strategy-tokens';

// Matches the real report's polling cadence (use-portfolio-performance-report.tsx).
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

// Live-price poll used to inject a "today" figure (see injectLiveDay) — same
// cadence family as the other live minute-candle polls in this codebase
// (locked-token-header.tsx, use-mtd-candles.ts). A short lookback is plenty;
// we only need the latest minute close, not a history.
const LIVE_PRICE_REFRESH_MS = 30 * 1000;
const LIVE_PRICE_LOOKBACK_MS = 10 * 60 * 1000;

const tokenKeyOf = (token: ReferenceStrategyToken) => `${token.source}:${token.underlying}`;

// Fetches daily candles since 2020-01-01 for each of `selectedTokens` via the
// generic `candlestick` RPC (same account-agnostic feed the Market Data
// Overview page uses), building a synthetic "buy and hold 1 unit" report per
// token. Superseded the old useReferenceStrategies (one useReferenceStrategyReport
// hook call per token, unrolled by hand) now that the token universe is
// dynamic (~80+ tokens from list_prices, not a fixed 6) — calling a hook once
// per item in a runtime-sized array breaks rules-of-hooks, so this manages
// every token's report in ONE hook via a single Record<name, report> state.
//
// Only fetches for `selectedTokens` (not the full universe) — with ~80+
// tokens available but typically only a handful selected, fetching
// unconditionally for everything (as the old 6-token version did) would mean
// dozens of unnecessary candlestick calls every 5 minutes for tokens no
// client has chosen to look at.
export const useReferenceStrategyReports = (
  selectedTokens: ReferenceStrategyToken[],
): Record<string, IPortfolioPerformanceAccountReport> => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [reportsByName, setReportsByName] = useState<
    Record<string, IPortfolioPerformanceAccountReport>
  >({});
  // Keyed by `${source}:${underlying}` (tokenKeyOf), not account name — this
  // is populated by a poll keyed off the token, and mapped to account name
  // only when composing the final output below.
  const [livePriceByToken, setLivePriceByToken] = useState<Record<string, number>>({});

  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  // selectedTokens is often a freshly-filtered/mapped array at the call site
  // (unstable reference every render) — read it via a ref in the output
  // useMemo below so that memo is only keyed on tokensKey, not identity.
  const selectedTokensRef = useRef(selectedTokens);
  useEffect(() => {
    selectedTokensRef.current = selectedTokens;
  });

  // Stable key so the effect only re-runs when the actual SET of selected
  // tokens changes, not on every parent re-render (selectedTokens is often
  // freshly filtered/mapped each render at the call site).
  const tokensKey = useMemo(
    () =>
      selectedTokens
        .map((t) => `${t.source}:${t.underlying}`)
        .sort()
        .join(','),
    [selectedTokens],
  );

  useEffect(() => {
    if (!isConnected || selectedTokens.length === 0) return;
    let cancelled = false;
    const unsubs: (() => void)[] = [];

    const fetchOne = (token: ReferenceStrategyToken) => {
      const reqId = `PP_REFERENCE_${token.source}_${token.underlying}`;
      const now = new Date();
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const begin = new Date(Date.UTC(2020, 0, 1));

      wsRef.current.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (!resp?.result) return;

        const closes: DailyClose[] = (resp.result as string[][]).map((row) => ({
          dateStr: row[0].split(' ')[0],
          close: parseFloat(row[4]),
        }));
        const report = buildReferenceReport(closes);
        if (!report) return;
        setReportsByName((prev) => ({ ...prev, [token.account.name]: report }));
      });
      unsubs.push(() => {
        try {
          wsRef.current.unsubscribeFromChannel(reqId);
        } catch {
          /* ignore */
        }
      });

      wsRef.current.sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: reqId,
          method: 'candlestick',
          params: {
            begin: begin.toISOString(),
            end: end.toISOString(),
            interval: '24:00:00',
            source: token.source,
            underlying: token.underlying,
            label: 'PERPETUAL',
          },
        }),
      );
    };

    selectedTokens.forEach(fetchOne);
    const interval = window.setInterval(
      () => selectedTokens.forEach(fetchOne),
      REFETCH_INTERVAL_MS,
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      unsubs.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on tokensKey, not selectedTokens (see comment above)
  }, [tokensKey, isConnected]);

  useEffect(() => {
    if (!isConnected || selectedTokens.length === 0) return;
    let cancelled = false;
    const unsubs: (() => void)[] = [];

    const fetchLivePrice = (token: ReferenceStrategyToken) => {
      const key = tokenKeyOf(token);
      const reqId = `PP_REFERENCE_LIVE_${token.source}_${token.underlying}_${Date.now()}`;
      const end = new Date();
      const begin = new Date(end.getTime() - LIVE_PRICE_LOOKBACK_MS);

      wsRef.current.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled) return;
        wsRef.current.unsubscribeFromChannel(reqId);
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        const rows = resp?.result as string[][] | undefined;
        if (!Array.isArray(rows) || rows.length === 0) return;

        const livePrice = parseFloat(rows[rows.length - 1][4]);
        if (!Number.isFinite(livePrice) || livePrice <= 0) return;
        setLivePriceByToken((prev) => ({ ...prev, [key]: livePrice }));
      });
      unsubs.push(() => {
        try {
          wsRef.current.unsubscribeFromChannel(reqId);
        } catch {
          /* ignore */
        }
      });

      wsRef.current.sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: reqId,
          method: 'candlestick',
          params: {
            begin: begin.toISOString(),
            end: end.toISOString(),
            interval: '00:01:00',
            source: token.source,
            underlying: token.underlying,
            label: 'PERPETUAL',
          },
        }),
      );
    };

    selectedTokens.forEach(fetchLivePrice);
    const interval = window.setInterval(
      () => selectedTokens.forEach(fetchLivePrice),
      LIVE_PRICE_REFRESH_MS,
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      unsubs.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on tokensKey, not selectedTokens (see comment above)
  }, [tokensKey, isConnected]);

  return useMemo(() => {
    const result: Record<string, IPortfolioPerformanceAccountReport> = {};
    for (const token of selectedTokensRef.current) {
      const report = reportsByName[token.account.name];
      if (!report) continue;
      const livePrice = livePriceByToken[tokenKeyOf(token)];
      result[token.account.name] = livePrice ? injectLiveDay(report, livePrice) : report;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on tokensKey, not selectedTokens (see comment on selectedTokensRef above)
  }, [tokensKey, reportsByName, livePriceByToken]);
};
